import React, { useEffect } from 'react';
import { AuthService } from '../../services/auth.service';
import { secureStore } from '../../utils/secureStore';
import toast from 'react-hot-toast';
import {
  loadPrivateKey,
  importPublicKey,
  hybridEncrypt,
  hybridDecrypt,
  generateRSAKeyPair,
  exportPublicKey,
  exportPrivateKey,
  encryptPrivateKey
} from '../../utils/rsaCrypto';

/**
 * RSAKeySelfHealing Component
 * 
 * Background component that runs on the main VaultPage.
 * It verifies if the user's current RSA keypair matches (i.e. private key can decrypt
 * data encrypted by public key). If a mismatch is detected (e.g. from a partial migration),
 * it regenerates a new key pair, encrypts the private key, encrypts the vault key, and
 * updates the backend.
 */
const RSAKeySelfHealing: React.FC = () => {
    useEffect(() => {
        const performSelfHealingCheck = async () => {
            try {
                // Wait for secureStore keys to be loaded
                await secureStore.ensureKeysLoaded();
                
                let encryptionKey: string;
                let vaultKey: string;
                
                try {
                    encryptionKey = secureStore.getEncryptionKey();
                    vaultKey = secureStore.getVaultKey();
                } catch (e) {
                    // Keys not in secureStore/localStorage yet (not logged in or empty cache)
                    return;
                }

                if (!encryptionKey || !vaultKey) {
                    return;
                }

                console.log('🔒 Self-healing: Fetching user keys from backend...');
                const keyData = await AuthService.getUserKeys();
                
                if (!keyData.success || keyData.cryptoVersion !== 2 || !keyData.publicKey || !keyData.encryptedPrivateKey) {
                    console.log('🔒 Self-healing: User is not cryptoVersion 2 or does not have RSA keys stored yet.');
                    return;
                }

                console.log('🔒 Self-healing: Validating RSA key match and vault key decryptability...');
                let isMatch = false;
                try {
                    const privateKey = await loadPrivateKey(keyData.encryptedPrivateKey, encryptionKey);
                    const publicKey = await importPublicKey(keyData.publicKey);
                    
                    // 1. Verify keypair matches itself
                    const testMessage = 'self-healing-validation-test-' + Date.now();
                    const encrypted = await hybridEncrypt(testMessage, publicKey);
                    const decrypted = await hybridDecrypt(
                        encrypted.encryptedData,
                        encrypted.encryptedKey,
                        encrypted.iv,
                        privateKey,
                        encrypted.authTag
                    );
                    
                    if (decrypted === testMessage) {
                        console.log('🔒 Self-healing: RSA keypair matches itself. Checking vault key decryption...');
                        
                        // 2. Verify vault key decryptability and value correctness
                        if (keyData.vaultKeys) {
                            try {
                                const decryptedVaultKey = await hybridDecrypt(
                                    keyData.vaultKeys.encryptedData,
                                    keyData.vaultKeys.encryptedKey,
                                    keyData.vaultKeys.iv,
                                    privateKey,
                                    keyData.vaultKeys.authTag
                                );
                                
                                if (decryptedVaultKey === vaultKey) {
                                    isMatch = true;
                                    console.log('✅ Self-healing: RSA keys and vault key are perfectly matched. No healing required.');
                                } else {
                                    console.warn('⚠️ Self-healing: Decrypted vault key does not match local vault key!');
                                }
                            } catch (vaultDecryptionError) {
                                console.error('⚠️ Self-healing: Failed to decrypt vault key from database:', vaultDecryptionError);
                            }
                        } else {
                            console.warn('⚠️ Self-healing: No vaultKeys payload returned from backend!');
                        }
                    } else {
                        console.warn('⚠️ Self-healing: Decrypted test message did not match!');
                    }
                } catch (decryptionError) {
                    console.error('⚠️ Self-healing: Test decryption failed, keys are mismatched:', decryptionError);
                }

                if (!isMatch) {
                    console.log('🚨 Self-healing: RSA key mismatch detected. Regenerating keys...');
                    
                    // 1. Generate new RSA key pair
                    const keyPair = await generateRSAKeyPair();
                    
                    // 2. Export new public key PEM
                    const newPublicKeyPem = await exportPublicKey(keyPair.publicKey);
                    
                    // 3. Export and encrypt new private key
                    const newPrivateKeyBuffer = await exportPrivateKey(keyPair.privateKey);
                    const newEncryptedPrivateKey = await encryptPrivateKey(newPrivateKeyBuffer, encryptionKey);
                    
                    // 4. Encrypt current vault key with new public key
                    const encryptedVaultKeyPayload = await hybridEncrypt(vaultKey, keyPair.publicKey);
                    
                    // 5. Save to database
                    const result = await AuthService.resetRSAKeys(
                        newPublicKeyPem,
                        newEncryptedPrivateKey,
                        encryptedVaultKeyPayload
                    );

                    if (result.success) {
                        // 6. Update local sessionStorage for admin session consistency
                        sessionStorage.setItem('encryptedPrivateKey', JSON.stringify(newEncryptedPrivateKey));
                        sessionStorage.setItem('authKey', encryptionKey);
                        
                        console.log('✅ Self-healing: RSA keypair regenerated and stored successfully.');
                        toast.success('🔒 Account security settings healed successfully!', {
                            duration: 5000
                        });
                    } else {
                        console.error('❌ Self-healing: Failed to reset keys in database.');
                    }
                }
            } catch (err) {
                console.error('❌ Self-healing error:', err);
            }
        };

        // Delay execution slightly to ensure main page mounting/loading has finished
        const timer = setTimeout(() => {
            performSelfHealingCheck();
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    return null;
};

export default RSAKeySelfHealing;
